import { getState }            from '../store';
import { socket }              from '../socket';
import { registerScreen }      from '../router';
import { escHtml, fmtScore }   from '../ui';
import { openAuthSheet }       from '../auth-sheet';
import { getUser }             from '../auth';
import type { SessionResult }  from '../../../shared/types';

// ─── Screen ───────────────────────────────────────────────────────────────────

let lastResult: SessionResult | null = null;
let msgUnsub: (() => void) | null = null;

// Capture the session result when server sends it
socket.onMessage(msg => {
  if (msg.type === 'session_end') lastResult = msg.result;
});

registerScreen('session-end', {
  mount() {
    const screen = document.getElementById('screen-session-end')!;
    const { playerId } = getState();
    const result = lastResult;

    if (!result) {
      screen.innerHTML = `<p class="text-muted text-center" style="padding-top:40px">Loading results…</p>`;
      return;
    }

    const allEscaped  = result.survivors === result.totalPlayers;
    const noneEscaped = result.survivors === 0;

    const headline = noneEscaped
      ? 'No Survivors 💀'
      : allEscaped
      ? 'Everyone Escaped! 🎉'
      : `${result.survivors} Escaped 🌋`;

    const subline = noneEscaped
      ? 'The lava got everyone. Better luck next time!'
      : `${result.totalPlayers - result.survivors} caught by lava`;

    const volcanoIcon = noneEscaped ? '🌋' : allEscaped ? '🎉' : '🌋';

    screen.innerHTML = `
      <!-- Hero -->
      <div class="end__hero">
        <span class="end__volcano-icon">${volcanoIcon}</span>
        <h1 class="end__headline">${escHtml(headline)}</h1>
        <p class="end__subline">${escHtml(subline)}</p>
      </div>

      <!-- Individual outcomes -->
      <div class="end__outcomes">
        <p class="end__outcomes-title">Final Results</p>
        ${buildOutcomes(result, playerId)}
      </div>

      <!-- Team score -->
      <div class="card end__team-card">
        <p class="end__team-label">🌋 Session Score</p>
        <p class="end__team-score">${fmtScore(result.teamScore)}</p>
        <p class="end__team-multiplier">
          ${result.survivors}/${result.totalPlayers} survived ·
          <span>${result.survivalMultiplier.toFixed(1)}× multiplier</span>
        </p>
      </div>

      <!-- Arcade name prompt -->
      <div class="end__arcade">
        <p class="end__arcade-title">Name your crew</p>
        <p class="end__arcade-sub">Save your score to the leaderboard</p>
        <input
          class="end__name-input"
          id="end-team-name"
          type="text"
          placeholder="YOUR CREW"
          maxlength="24"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="characters"
          spellcheck="false"
        />
      </div>

      <!-- Actions -->
      <div class="end__actions">
        <button class="btn btn--primary" id="btn-save-score">
          🏆 Save to Leaderboard
        </button>
        <button class="btn btn--outline" id="btn-play-again">
          🌋 Play Again
        </button>
        <button class="btn btn--ghost text-sm" id="btn-skip-save">
          Skip
        </button>
      </div>
    `;

    // Save score → auth flow if needed
    document.getElementById('btn-save-score')!.addEventListener('click', () => {
      const name = (document.getElementById('end-team-name') as HTMLInputElement).value.trim();
      if (name.length < 2) {
        const input = document.getElementById('end-team-name') as HTMLInputElement;
        input.focus();
        input.style.borderColor = 'var(--c-accent)';
        return;
      }
      handleSave(name);
    });

    // Play Again — same room, reset volcano
    document.getElementById('btn-play-again')!.addEventListener('click', () => {
      const { myPlayer } = getState();
      if (myPlayer?.isHost) socket.send({ type: 'host_start' });
    });

    document.getElementById('btn-skip-save')!.addEventListener('click', () => {
      // Just show play again / leave options, no save
      hideSaveSection();
    });

    // Focus name input
    setTimeout(() => {
      (document.getElementById('end-team-name') as HTMLInputElement | null)?.focus();
    }, 800);
  },

  unmount() {
    msgUnsub?.();
    msgUnsub = null;
  },
});

// ─── Outcome rows ─────────────────────────────────────────────────────────────

function buildOutcomes(result: SessionResult, myPlayerId: string | null): string {
  const alive  = result.players.filter(p => p.status !== 'ghost');
  const ghosts = result.players.filter(p => p.status === 'ghost');

  const row = (p: (typeof result.players)[0], rank: number): string => {
    const isMe    = p.playerId === myPlayerId;
    const isGhost = p.status === 'ghost';
    const isRescued = p.status === 'rescued';

    const icon  = isGhost   ? '💀'
                : isRescued ? '🪙'
                : rank === 1 ? '🥇'
                : rank === 2 ? '🥈'
                : rank === 3 ? '🥉'
                : '✅';

    const badge = isGhost   ? '<span class="end__outcome-badge end__outcome-badge--ghost">Caught</span>'
                : isRescued ? '<span class="end__outcome-badge end__outcome-badge--rescued">Rescued 🪙</span>'
                : '<span class="end__outcome-badge end__outcome-badge--escaped">Escaped</span>';

    return `
      <div class="end__outcome-row
                  ${isGhost  ? 'end__outcome-row--ghost' : ''}
                  ${isMe     ? 'end__outcome-row--me'    : ''}">
        <span class="end__outcome-icon">${icon}</span>
        <span class="end__outcome-name">${escHtml(p.username)}</span>
        <span class="end__outcome-score">${fmtScore(p.score)}</span>
        ${badge}
      </div>
    `;
  };

  const divider = ghosts.length > 0
    ? `<div class="results__divider">Caught by lava</div>`
    : '';

  return alive.map((p, i) => row(p, i + 1)).join('') +
         divider +
         ghosts.map(p => row(p, 99)).join('');
}

// ─── Save flow ────────────────────────────────────────────────────────────────

function handleSave(teamName: string): void {
  const isLoggedIn = !!getUser();

  if (!isLoggedIn) {
    sessionStorage.setItem('pending_team_name', teamName);
    openAuthSheet(() => { saveTeamScore(teamName); });
    return;
  }

  saveTeamScore(teamName);
}

async function saveTeamScore(teamName: string): Promise<void> {
  const { roomCode } = getState();
  if (!lastResult || !roomCode) return;

  const saveBtn = document.getElementById('btn-save-score') as HTMLButtonElement | null;
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const user = getUser();
    if (user) headers['Authorization'] = `Bearer ${user.idToken}`;

    const res = await fetch(`/sessions/${roomCode}/results`, {
      method:  'POST',
      headers,
      body: JSON.stringify({ teamName, result: lastResult }),
    });

    if (!res.ok) throw new Error(await res.text());

    if (saveBtn) { saveBtn.textContent = '✅ Saved!'; }
  } catch (err) {
    console.error('Save failed', err);
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = '🏆 Save to Leaderboard'; }
    return;
  }

  hideSaveSection();
}


function hideSaveSection(): void {
  const arcade  = document.querySelector('.end__arcade');
  const saveBtn = document.getElementById('btn-save-score');
  const skipBtn = document.getElementById('btn-skip-save');
  arcade?.remove();
  saveBtn?.remove();
  skipBtn?.remove();
}
